// import React from "react";
import dayjs from "dayjs";
import { getStorage, ref, getMetadata } from "firebase/storage";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SidebarMenus from "../components/SideBar/admin.routes";
import { ROLES } from "../config/Roles.config";
import cryptoJs from "../utils/cryptoJs/crypto";
dayjs.extend(customParseFormat);

// function which returns first name of logged in user
export const extractFirstName = (email) => {
  const atIndex = email.indexOf("@");
  if (atIndex !== -1) {
    const firstPart = email.substring(0, atIndex);
    const firstName = firstPart.split(".")[0];
    return firstName;
  }
  // If "@" is not found, return the entire email address
  return email;
};

// Function to capitalize first letter of each word
export const capitalizeFirstLetterOfEachWord = (str) => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}


// Function used to capitailize a text
export const replaceTextToCaplitalize = (inputValue) => {
  return inputValue.charAt(0).toUpperCase() + inputValue.slice(1);    
};

// Handle input text validation
export const handleValidationOnInput = e => {
  ;["e", "E", "+", "-", ".", " "].includes(e.key) && e.preventDefault()
}

// Function to return date with current time stamp
export const addCurrentTimeInTimeStamp = (timestamp) => {
  const now = new Date();
  const date = new Date(timestamp);
  date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  return date.toISOString();
};

// Get Page Title Name (Current path/route)
export const getPageTitle = () => {
  const role = ROLES.ADMIN;
  const activePath = window.location.pathname;

  for (const menu of SidebarMenus) {
    if (!menu.role.includes(role)) continue;

    // If menu has children, check each child path
    if (menu.children?.length > 0) {
      for (const child of menu.children) {
        if (child.path === activePath && child.role.includes(role)) {
          return { pageName: child.label };
        }
      }
    }

    // If no children, check top-level path
    if (menu.path === activePath) {
      return { pageName: menu.label };
    }
  }

  return { pageName: "" };
};

// Function to check if the url is a firebase storage url
export const isFirebaseUrl = (url) =>
  typeof url === "string" && url.includes("firebasestorage.googleapis.com");

// extract file name from firebase storage image url
export const getFileNameFromInput = (input) => {
  // If it's a File object (blob or any File)
  if (typeof input !== "string") {
    const { name } = input;
    return name;
  }

  // If it's a Firebase URL
  if (typeof input === "string") {
    try {
      const decodedURL = decodeURIComponent(input);
      const start = decodedURL.lastIndexOf("/") + 1;
      const end = decodedURL.indexOf("?") !== -1 ? decodedURL.indexOf("?") : decodedURL.length;
      const fileName = decodedURL.substring(start, end);
      return fileName;
    } catch (error) {
      console.error("Error extracting file name:", error);
      return "";
    }
  }

  return "";
};

// Function used to check the duplicate entity in a data table
export const checkDuplicateEntity = (existingData, entity) => {
  if (existingData && existingData.includes(entity.trim().toLowerCase())) {
    return true;
  } else {
    return false;
  }
}

// Function to generate random Id
export const generateRandomId = (length = 15) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let randomId = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    randomId += characters[randomIndex];
  }
  return randomId;
};

// Function to get the size of uploaded file in MB (.png, ,jpeg, .docx, .xlsx etc)
export const getFileSize = async (input, decimals = 2) => {
  const formatSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // If it's already a number (bytes), format it directly
  if (typeof input === "number") {
    return formatSize(input);
  }

  // If it's a Firebase URL
  if (typeof input === "string" && input.startsWith("http")) {
    const storage = getStorage();
    // Create a reference from the download URL
    const fileRef = ref(storage, input);
    const metadata = await getMetadata(fileRef);
    return formatSize(metadata.size);
  }

  throw new Error("Invalid input: must be bytes or Firebase Storage download URL");
}

// Common function to sort data according to requirement
export const sortData = (data, entity, sortType = "asc") => {
  if (!Array.isArray(data)) {
    throw new Error("The data parameter must be an array.");
  }

  return [...data].sort((a, b) => {
    if (a[entity] === undefined || b[entity] === undefined) {
      throw new Error(`The entity "${entity}" is not present in the data.`);
    }

    const comparison = a[entity].toString().localeCompare(b[entity].toString(), undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return sortType === "asc" ? comparison : -comparison;
  });
};

// Set the required data into dropdown/select field
export const setRequiredDataForDropdown = (setter, data, labelKey, valueKey, additionalFields = []) => {
  if (!Array.isArray(data) || data.length === 0) {
    setter([]); // Set empty array if data is missing or invalid
    return;
  }

  const formattedData = data.map(item => {
    let formattedItem = {
      label: item[labelKey] || "",
      value: item[valueKey] || "",
    };

    // Add additional fields if provided
    if (Array.isArray(additionalFields) && additionalFields?.length > 0) {
      additionalFields.forEach(field => {
        if (item.hasOwnProperty(field)) {
          formattedItem[field] = item[field];
        }
      });
    }

    return formattedItem;
  });

  setter(formattedData);
};

// Function to sort data alphabetically
export const sortDataAlphabetically = (data, key) => {
  if (!Array.isArray(data) || data.length === 0) return []; // Return empty array if no data
  return [...data].sort((a, b) => (a[key]?.localeCompare(b[key]) || 0));
};

// Function to get data if "All" is selected for branch & dept
export const getDataForAllOption = (type, id, inputData) => {
  if (id === "All") {
    if (type === "branch") {
      const data =
        inputData && inputData[0]
          ? inputData
              .filter((branch) => branch.value !== "All")
              .map((branch) => branch.value)
          : [];
      return data;
    } else if (type === "department") {
      const data =
        inputData && inputData[0]
          ? inputData
              .filter((dept) => dept.value !== "All")
              .map((dept) => dept.value)
          : [];
      return data;
    }
  } else {
    return [id];
  }
};

// Function to get changed fields
export const getChangedFields = (current, original, formatFn = {}) => {
  return Object.keys(current).reduce((diff, key) => {
    const currentValue = formatFn[key] ? formatFn[key](current[key]) : current[key];
    const originalValue = formatFn[key] ? formatFn[key](original[key]) : original[key];

    if (currentValue !== originalValue) {
      diff[key] = currentValue;
    }
    return diff;
  }, {});
};


// Custom function to handle error message
export const getErrorMessage = (error, isUpdateMode, messages) => {
  if (error?.error) return error.error;
  if (error?.message) return error.message;
  return isUpdateMode ? messages.UPDATE.FAILED : messages.CREATE.FAILED;
};

// Reusable patterns
const urlPatterns = {
  vimeo: /^https?:\/\/([^\/]+\.)?vimeo\.com/i,
  heyzine: /^https?:\/\/([^\/]+\.)?heyzine\.com/i,
};

// Helper to validate URL
export const isValidMediaUrl = (url) => {
  return urlPatterns.vimeo.test(url) || urlPatterns.heyzine.test(url);
};


// Session helpers
export const setEntity = (data, key) => {
  let authKey = key ? key : process.env.REACT_APP_ENTITY_DETAILS;
  const encrypted = cryptoJs.encryptData(JSON.stringify(data));
  sessionStorage.setItem(authKey, encrypted);
};

export const getEntity = (key) => {
  let authKey = key ? key : process.env.REACT_APP_ENTITY_DETAILS;
  const encrypted = sessionStorage.getItem(authKey);
  return encrypted ? JSON.parse(cryptoJs.decryptData(encrypted)) : null;
};

export const removeEntity = (key) => {
  let authKey = key ? key : process.env.REACT_APP_ENTITY_DETAILS;
  sessionStorage.removeItem(authKey);
};